import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminSystemLogs = (initialFilters = {}) => {
  const [filters, setFilters] = useState({
    action_type: '',
    log_type: '',
    admin_id: '',
    user_id: '',
    date_from: '',
    date_to: '',
    ...initialFilters
  });

  const logTypeOptions = [
    { value: '', label: 'All Logs' },
    { value: 'admin_action', label: 'Admin Actions' },
    { value: 'user_activity', label: 'User Activities' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: systemLogsResponse, isLoading, error } = useQuery({
    queryKey: ['adminSystemLogs', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/system-logs${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  // Fetch statistics
  const { data: statsResponse, isLoading: isLoadingStats } = useQuery({
    queryKey: ['adminSystemLogsStats'],
    queryFn: async () => {
      const response = await api.get('/admin/system-logs/statistics');
      return response.data;
    }
  });

  const systemLogs = useMemo(() => {
    const data = systemLogsResponse?.data?.data || systemLogsResponse?.data;
    if (data && Array.isArray(data)) {
      return data;
    }
    if (data && data.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  }, [systemLogsResponse]);

  const statistics = useMemo(() => {
    return statsResponse?.data || {};
  }, [statsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(log => 
      log.action_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.ip_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.admin && (log.admin.name?.toLowerCase().includes(searchTerm.toLowerCase()) || log.admin.email?.toLowerCase().includes(searchTerm.toLowerCase()))) ||
      (log.user && (log.user.name?.toLowerCase().includes(searchTerm.toLowerCase()) || log.user.email?.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  };

  const getLogTypeColor = (logType) => {
    return logType === 'admin_action' 
      ? 'bg-blue-100 text-blue-800' 
      : 'bg-purple-100 text-purple-800';
  };

  return {
    systemLogs,
    isLoading: isLoading || isLoadingStats,
    error,
    filters,
    logTypeOptions,
    handleFilterChange,
    handleSearch,
    getLogTypeColor,
    statistics
  };
};

